import { IconMapPin } from '@tabler/icons-react';

interface AddressDisplayProps {
  addressLine?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

const formatCountry = (country: string) =>
  country.length > 3
    ? country.charAt(0).toUpperCase() + country.slice(1).toLowerCase()
    : country.toUpperCase();

/**
 * Compact address: street on the first line, postal code + city on the second
 * one in a smaller font, and the country as a discreet chip.
 * Hook free: can be called as a function (`AddressDisplay({...})`).
 */
export const AddressDisplay = ({
  addressLine,
  postalCode,
  city,
  country,
}: AddressDisplayProps) => {
  const locality = [postalCode, city].filter(Boolean).join(' ');

  if (!addressLine && !locality && !country) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex items-start gap-2 min-w-0">
      <IconMapPin
        size={15}
        stroke={1.8}
        className="mt-[2px] shrink-0 text-gray-400"
      />
      <div className="flex min-w-0 flex-col gap-0.5 leading-snug">
        {addressLine && (
          <span className="text-[13px] text-black-071336 dark-mode:text-gray-700">
            {addressLine}
          </span>
        )}
        {(locality || country) && (
          <span className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
            {locality && <span>{locality}</span>}
            {country && (
              <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-[1px] text-[10.5px] font-medium uppercase tracking-wide text-gray-500 dark-mode:bg-coal-500 dark-mode:border-gray-300">
                {formatCountry(country)}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
};
